import { useCallback, useEffect, useState } from "react";

const STORAGE_COLLAPSED = "imobgo.sidebar.collapsed";
const STORAGE_HIDDEN = "imobgo.sidebar.hidden";
const TABLET_QUERY = "(max-width: 1279px) and (min-width: 1024px)";

type Mode = "manual" | "auto";

interface State {
  collapsed: boolean;
  hidden: boolean;
  mode: Mode;
}

function readBool(key: string): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(key);
    if (v === "true") return true;
    if (v === "false") return false;
  } catch {
    /* ignore */
  }
  return null;
}

function writeBool(key: string, value: boolean) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* ignore */
  }
}

function loadInitial(): State {
  if (typeof window === "undefined") {
    return { collapsed: false, hidden: false, mode: "auto" };
  }
  const storedCollapsed = readBool(STORAGE_COLLAPSED);
  const storedHidden = readBool(STORAGE_HIDDEN);
  if (storedCollapsed !== null || storedHidden !== null) {
    return {
      collapsed: storedCollapsed ?? false,
      hidden: storedHidden ?? false,
      mode: "manual",
    };
  }
  const isTablet = window.matchMedia(TABLET_QUERY).matches;
  return { collapsed: isTablet, hidden: false, mode: "auto" };
}

/**
 * Gerencia 3 estados do sidebar:
 * - expanded   (collapsed=false, hidden=false) — 260px
 * - collapsed  (collapsed=true,  hidden=false) — 76px mini-rail
 * - hidden     (hidden=true)                   — 0px (slide-out à esquerda)
 *
 * Persiste preferência manual em localStorage. Sem preferência, segue
 * media-query (tablet → collapsed; desktop → expanded). Mobile usa
 * drawer overlay separado, não consome esse hook.
 */
export function useSidebarMode() {
  const [state, setState] = useState<State>(loadInitial);

  useEffect(() => {
    if (state.mode !== "auto") return;
    const mq = window.matchMedia(TABLET_QUERY);
    const handler = (e: MediaQueryListEvent) => {
      setState((prev) =>
        prev.mode === "auto" ? { ...prev, collapsed: e.matches } : prev,
      );
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [state.mode]);

  const toggleCollapse = useCallback(() => {
    setState((prev) => {
      // Toggle só atua quando visível; se hidden, primeiro mostra
      if (prev.hidden) {
        writeBool(STORAGE_HIDDEN, false);
        return { ...prev, hidden: false, mode: "manual" };
      }
      const next = !prev.collapsed;
      writeBool(STORAGE_COLLAPSED, next);
      return { ...prev, collapsed: next, mode: "manual" };
    });
  }, []);

  const toggleHidden = useCallback(() => {
    setState((prev) => {
      const next = !prev.hidden;
      writeBool(STORAGE_HIDDEN, next);
      return { ...prev, hidden: next, mode: "manual" };
    });
  }, []);

  const show = useCallback(() => {
    setState((prev) => {
      if (!prev.hidden) return prev;
      writeBool(STORAGE_HIDDEN, false);
      return { ...prev, hidden: false, mode: "manual" };
    });
  }, []);

  return {
    collapsed: state.collapsed,
    hidden: state.hidden,
    mode: state.mode,
    toggleCollapse,
    toggleHidden,
    show,
  };
}
